import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import KrakenLoader from "@/components/KrakenLoader";

const STATUS_COLOR = {
  paid: "bg-green-500/15 text-green-300 border-green-400/40 status-success",
  partial: "bg-yellow-500/15 text-yellow-300 border-yellow-400/40",
  unpaid: "bg-slate-500/15 text-slate-400 border-slate-400/30 status-missing",
  overdue: "bg-red-500/15 text-red-300 border-red-400/40 status-missing",
  waived: "bg-blue-500/15 text-blue-300 border-blue-400/40",
};

const CAT_LABEL = {
  team_dues: "Team Dues", uniform_fees: "Uniform Fees", tournament_fees: "Tournament Fees",
  camps: "Camps", sponsorships: "Sponsorships", donations: "Donations", merch: "Merch", other: "Other",
};

function Badge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 inline-flex items-center gap-1.5 ${STATUS_COLOR[status] || ""}`}>
      {status === "paid" && <Check size={10} className="check-pop" />}
      {status}
    </span>
  );
}

const payNow = () =>
  toast.info("Online payments are coming soon. Reach out to your coach for payment instructions in the meantime.");

export default function Payments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/payments/mine").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const due = items
    .filter((p) => ["unpaid", "partial", "overdue"].includes(p.status))
    .reduce((s, p) => s + (Number(p.remaining_balance) || 0), 0);
  const paid = items.reduce((s, p) => s + (Number(p.amount_paid) || 0), 0);
  const hasFees = items.length > 0;

  return (
    <div data-testid="payments-page">
      <div className="eyebrow mb-1">Payments</div>
      <h1 className="font-display text-2xl sm:text-3xl uppercase mb-5">Account Balance</h1>

      {loading ? (
        <KrakenLoader label="Loading payments…" testId="payments-loader" />
      ) : (
        <>
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5">
        <div className="kr-card rounded-xl p-3 sm:p-5">
          <div className="eyebrow text-[10px]">Balance Due</div>
          <div className="font-display text-xl sm:text-3xl text-[#00E5FF] mt-1 break-all">${due.toFixed(2)}</div>
        </div>
        <div className="kr-card rounded-xl p-3 sm:p-5">
          <div className="eyebrow text-[10px]">Total Paid</div>
          <div className="font-display text-xl sm:text-3xl mt-1 break-all">${paid.toFixed(2)}</div>
        </div>
        <div className="kr-card rounded-xl p-3 sm:p-5">
          <div className="eyebrow text-[10px]">Requests</div>
          <div className="font-display text-xl sm:text-3xl mt-1">{items.length}</div>
        </div>
      </div>

      {!hasFees ? (
        <div className="kr-card rounded-xl p-8 sm:p-12 text-center" data-testid="payments-empty">
          <div className="font-display text-xl sm:text-2xl uppercase text-[#00E5FF]">No payment requests at this time</div>
          <p className="text-slate-400 mt-2 text-sm">Any dues, tournament fees, or other charges your coach assigns will appear here.</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {items.map((p) => (
              <div key={p.id} className="kr-card rounded-xl p-4" data-testid={`pay-${p.id}-mobile`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{p.title || p.label}</div>
                    {p.description && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{p.description}</div>}
                  </div>
                  <Badge status={p.status} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mt-3">
                  <div><span className="text-slate-500">Category:</span> <span className="text-slate-300">{CAT_LABEL[p.category] || p.category || "—"}</span></div>
                  <div><span className="text-slate-500">Due:</span> <span className="text-slate-300">{p.due_date || "—"}</span></div>
                  <div><span className="text-slate-500">Amount:</span> <span className="text-white">${Number(p.amount).toFixed(2)}</span></div>
                  <div><span className="text-slate-500">Paid:</span> <span className="text-green-300">${Number(p.amount_paid || 0).toFixed(2)}</span></div>
                  {Number(p.remaining_balance) > 0 && (
                    <div className="col-span-2"><span className="text-slate-500">Remaining:</span> <span className="text-[#00E5FF]">${Number(p.remaining_balance).toFixed(2)}</span></div>
                  )}
                </div>
                {["unpaid", "partial", "overdue"].includes(p.status) && (
                  <button onClick={payNow} className="btn-primary w-full justify-center text-xs mt-3" data-testid={`pay-now-${p.id}-mobile`}>
                    Pay Now
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block kr-card rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-[#14213D] uppercase text-xs tracking-widest text-slate-300">
                <tr><th className="text-left p-4">Item</th><th className="text-left p-4">Category</th><th className="text-left p-4">Due</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Status</th><th className="text-left p-4"></th></tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-white/10" data-testid={`pay-${p.id}`}>
                    <td className="p-4 font-semibold">
                      {p.title || p.label}
                      {p.description && <div className="text-xs text-slate-400 mt-1">{p.description}</div>}
                    </td>
                    <td className="p-4 text-slate-300 text-xs uppercase tracking-widest">{CAT_LABEL[p.category] || p.category || "—"}</td>
                    <td className="p-4 text-slate-400">{p.due_date || "—"}</td>
                    <td className="p-4">
                      ${Number(p.amount).toFixed(2)}
                      {Number(p.remaining_balance) > 0 && Number(p.remaining_balance) < Number(p.amount) && (
                        <div className="text-xs text-yellow-400 mt-1">${Number(p.remaining_balance).toFixed(2)} remaining</div>
                      )}
                    </td>
                    <td className="p-4"><Badge status={p.status} /></td>
                    <td className="p-4 text-right">
                      {["unpaid", "partial", "overdue"].includes(p.status) && (
                        <button onClick={payNow} className="btn-ghost text-xs" data-testid={`pay-now-${p.id}`}>Pay Now</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}
